/**
 * Phase 120 (Plan 120-01) — release-flags API client.
 *
 * Backend contract: `GET /api/release-flags` (`authenticate`-only,
 * `backend/src/api/routes/modules/core.ts`) — `{ data: { flags: { <key>: boolean } } }`,
 * the resolved on/off state of every flag in the running build's registry.
 * A key the build does not know is simply absent (and `useFlag` treats it as off).
 */
import { apiGet, apiPost, apiDelete } from './client';

export async function fetchReleaseFlags(): Promise<Record<string, boolean>> {
  const res = await apiGet<{ data: { flags: Record<string, boolean> } }>('/release-flags');
  return res.data.flags;
}

// ---------------------------------------------------------------------------
// Plan 120-02 — admin surface (`/api/admin/release-flags`, admin-only)
// ---------------------------------------------------------------------------

export type ReleaseFlagSource = 'override' | 'verified' | 'default';
export type ReleaseFlagStaleTier = 'ok' | 'warn' | 'fail';

export interface AdminReleaseFlagTaskRef {
  id: number;
  projectId: number;
  code: string;
  name: string;
  stateName: string | null;
  isTerminal: boolean;
}

/** Mirrors `AdminFlagRow` in `backend/src/services/releaseFlagService.ts`. */
export interface AdminReleaseFlagRow {
  key: string;
  ticket: string;
  description: string;
  /** ISO date `YYYY-MM-DD` (UTC). */
  addedAt: string;
  /** Whole UTC days since `addedAt`; null when `addedAt` is unparsable. */
  ageDays: number | null;
  staleTier: ReleaseFlagStaleTier;
  effective: boolean;
  source: ReleaseFlagSource;
  /** What the flag would be with no override and no verification, in this environment. */
  envDefault: boolean;
  /** `byUserName` is the actor's "Lastname Firstname"; null when the user row is gone (MKS-197). */
  override: {
    enabled: boolean;
    byUserId: number | null;
    byUserName: string | null;
    at: string | null;
  } | null;
  verified: {
    byUserId: number | null;
    byUserName: string | null;
    at: string | null;
    source: string | null;
    cleanupTaskId: number | null;
  } | null;
  /** The task carrying the ticket code, only when exactly one does. */
  task: AdminReleaseFlagTaskRef | null;
  cleanupTask: AdminReleaseFlagTaskRef | null;
  /**
   * MKS-273 — who filed the ticket's task, in the page's "Lastname Firstname"
   * convention (an external creator's own name, `Robot` for a machine user).
   * Null when the ticket has no task, several tasks, or no creator stamp.
   */
  creatorName: string | null;
  creatorKind: 'user' | 'external_user' | 'machine' | null;
}

export interface ReleaseFlagStateResult {
  key: string;
  ticket: string;
  effective: boolean;
  source: ReleaseFlagSource;
}

export async function fetchAdminReleaseFlags(): Promise<AdminReleaseFlagRow[]> {
  const res = await apiGet<{ data: AdminReleaseFlagRow[] }>('/admin/release-flags');
  return res.data;
}

/** POST /api/admin/release-flags/:key/switch-off — explicit `enabled:false` override, audited. */
export async function switchReleaseFlagOff(key: string): Promise<ReleaseFlagStateResult> {
  const res = await apiPost<{ data: ReleaseFlagStateResult }>(
    `/admin/release-flags/${encodeURIComponent(key)}/switch-off`
  );
  return res.data;
}

/** DELETE /api/admin/release-flags/:key/override — back to verified/default (idempotent). */
export async function resetReleaseFlagOverride(key: string): Promise<ReleaseFlagStateResult> {
  const res = await apiDelete<{ data: ReleaseFlagStateResult }>(
    `/admin/release-flags/${encodeURIComponent(key)}/override`
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Plan 120-03 — "Switch on (mark verified)"
// ---------------------------------------------------------------------------

export type ReleaseFlagReconcileStatus =
  | 'staging_skipped'
  | 'not_in_running_registry'
  | 'not_verified'
  | 'task_missing'
  | 'task_ambiguous'
  | 'flags_not_all_on'
  | 'reconciled'
  | 'failed';

/** Mirrors `SwitchTicketOnResult` in `backend/src/services/releaseFlagLifecycleService.ts`. */
export interface ReleaseFlagSwitchOnResult {
  ticket: string;
  verified: boolean;
  newlyVerified: boolean;
  clearedOverrides: string[];
  reconcile: {
    status: ReleaseFlagReconcileStatus;
    /** `moved` or the reason the task was left alone; null when no move was attempted. */
    taskDone: string | null;
    cleanupTaskId: number | null;
  };
}

/**
 * POST /api/admin/release-flags/tickets/:ticket/verify — clears the ticket's
 * overrides, marks it verified (one-way) and moves its task to Done when the
 * running build carries its flags.
 */
export async function switchReleaseFlagTicketOn(
  ticket: string
): Promise<ReleaseFlagSwitchOnResult> {
  const res = await apiPost<{ data: ReleaseFlagSwitchOnResult }>(
    `/admin/release-flags/tickets/${encodeURIComponent(ticket)}/verify`
  );
  return res.data;
}
